import React from 'react';
import { Formik, Field } from "formik";
import { Alert, Badge, Button, Card, Table } from 'react-bootstrap';
import { Auth, API } from "aws-amplify";
import classnames from 'classnames';

import './SetRoadPackages.css';

// Radio input
const RadioButton = ({
    field: { name, value, onChange, onBlur },
    id,
    label,
    className,
    ...props
}) => {
    return (
        <div>
            <input
                name={name}
                id={id}
                type="radio"
                value={id}
                checked={id === value}
                onChange={onChange}
                onBlur={onBlur}
                className={classnames("radio-button")}
                {...props}
            />
            <label htmlFor={id}>{label}</label>
        </div>
    );
};

const InputFeedback = ({ error }) =>
    error ? <div className="input-feedback">{error}</div> : null;

const RadioButtonGroup = ({
    value,
    error,
    touched,
    id,
    label,
    className,
    children
}) => {
    const classes = classnames(
        "input-field",
        {
            "is-success": value || (!error && touched),
            "is-error": !!error
        },
        className
    );
    return (
        <div className={classes}>
            <fieldset>
                <legend>{label}</legend>
                {children}
                {touched && <InputFeedback error={error} />}
            </fieldset>
        </div>
    );
};

export default class Registration extends React.Component {
    constructor(props) {
        super(props);
        this.onChange = this.onChange.bind(this);
        this.state = {
            packages: [],
            loading: false,
            values: {},
            errors: {},
            touched: {}
        };
    }

    fetchRoadTestPackages() {
        const that = this;
        fetch('https://s3.amazonaws.com/publicdates/roadTestPackages.json', {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            method: 'GET'
        })
            .then(this.handleErrors)
            .then(function (response) {
                return response.json();
            }).then(function (data) {
                if (typeof (data) === "undefined") {
                    that.setState({ packages: [] });
                } else {
                    that.setState({ packages: data });
                }
            });
    }

    componentDidMount() {
        this.fetchRoadTestPackages();
    }

    onChange(e, index, field) {
        var localPackages = this.state.packages;
        switch (field) {
            case "name":
                localPackages[index].package = e;
                break;
            case "price":
                localPackages[index].price = e;
                break;
            case "lessons":
                localPackages[index].lessons = e;
                break;
            default:
                console.warn("Default value hit. Should not reach here.");
        }
        this.setState({ packages: localPackages });
    }

    addNewPackage = () => {
        var localPackages = this.state.packages;
        localPackages.push({
            "package": "",
            "price": "",
            "editName": true,
            "lessons": 0
        });
        this.setState({ packages: localPackages, refreshStatus: "", packageError: "" });
    };

    deletePackage = (index) => {
        var localPackages = this.state.packages;
        localPackages.splice(index, 1);
        this.setState({ packages: localPackages, refreshStatus: "", packageError: "" });
    };

    handleErrors = (response) => {
        if (!response.ok) {
            this.setState({ loading: false, packageError: "There was an error: " + response.statusText });
        }
        return response;
    };

    handleSubmit = async event => {
        this.setState({ loading: true, refreshStatus: "", packageError: "" });
        let packages = this.state.packages.filter(x => x.package.length > 0).sort((a, b) => (a.package > b.package) ? 1 : ((b.package > a.package) ? -1 : 0));
        const that = this;
        let session = await Auth.currentSession();
        let options = {
            body: packages,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': session.idToken.jwtToken
            }
        };
        API.post("APIGateway", "/updateroadtestpackages ", options)
            .then(function (data) {
                that.setState({ loading: false, refreshStatus: "Successfully updated!" });
            }).catch(function (error) {
                console.log(error);
                if (error.response) {
                    console.log(error.response.data);
                    console.log(error.response.status);
                    console.log(error.response.headers);
                } else if (error.request) {
                    console.log(error.request);
                } else {
                    console.log('Error', error.message);
                }
                that.setState({ loading: false, packageError: "There was an error saving packages. Please try again." });
            });
    };

    render() {
        const { packages, loading, refreshStatus, packageError } = this.state;

        return (
            <div className="holder">
                {/* Alerts */}
                {packageError && (
                    <Alert variant="warning" onClose={() => this.setState({ packageError: "" })} dismissible>
                        <strong>{packageError}</strong>
                    </Alert>
                )}
                {refreshStatus && (
                    <Alert variant="success" onClose={() => this.setState({ refreshStatus: "" })} dismissible>
                        <strong>{refreshStatus}</strong>
                    </Alert>
                )}

                {/* Page header */}
                <div style={{ marginBottom: "1.25rem" }}>
                    <h3 style={{ marginBottom: "0.25rem" }}>Set Active Packages</h3>
                    <small className="text-muted">Be careful with package names — duplicates will cause issues on the registration form.</small>
                </div>

                {/* Packages table */}
                <Card style={{ marginBottom: "1.5rem" }}>
                    <Card.Header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>
                            <strong>Packages</strong>
                            {packages.length > 0 && (
                                <Badge variant="secondary" style={{ marginLeft: "0.5rem" }}>
                                    {packages.length}
                                </Badge>
                            )}
                        </span>
                        <Button variant="success" size="sm" disabled={loading} onClick={this.addNewPackage}>
                            + Add New
                        </Button>
                    </Card.Header>
                    <Card.Body style={{ padding: 0 }}>
                        {packages.length === 0 ? (
                            <p className="text-muted" style={{ padding: "1rem", margin: 0 }}>
                                No packages loaded. Click "+ Add New" to create one.
                            </p>
                        ) : (
                            <Table striped bordered hover size="sm" style={{ margin: 0 }}>
                                <thead className="thead-dark">
                                    <tr>
                                        <th>Package Name</th>
                                        <th>Cost ($)</th>
                                        <th>Lessons</th>
                                        <th style={{ width: "80px" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {packages.map((x, index) => {
                                        const disabled = !x.editName || loading;
                                        return (
                                            <tr key={index}>
                                                <td>
                                                    <input
                                                        className="form-control form-control-sm inputWidth"
                                                        type="text"
                                                        disabled={disabled}
                                                        onChange={e => this.onChange(e.target.value, index, "name")}
                                                        value={x.package}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="form-control form-control-sm inputWidth"
                                                        type="text"
                                                        disabled={loading}
                                                        onChange={e => this.onChange(e.target.value, index, "price")}
                                                        value={x.price}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="form-control form-control-sm inputWidth"
                                                        type="text"
                                                        disabled={loading}
                                                        onChange={e => this.onChange(e.target.value, index, "lessons")}
                                                        value={x.lessons}
                                                    />
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        disabled={disabled}
                                                        onClick={() => this.deletePackage(index)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                {/* Save */}
                <div style={{ marginBottom: "1.5rem" }}>
                    <Button variant="primary" disabled={loading} onClick={this.handleSubmit}>
                        {loading ? "Saving…" : "Save Packages"}
                    </Button>
                </div>

                {/* Form Preview */}
                <Card>
                    <Card.Header><strong>Public Form Preview</strong></Card.Header>
                    <Card.Body>
                        <p className="text-muted" style={{ marginBottom: "0.5rem" }}>
                            Shows how packages appear on the registration form.
                        </p>
                        <Formik
                            initialValues={{}}
                            onSubmit={this.bindSubmitForm}
                        >
                            {props => {
                                const { values, touched, errors, handleSubmit } = props;
                                return (
                                    <form onSubmit={handleSubmit}>
                                        <RadioButtonGroup
                                            id="roadTestPackage"
                                            label="Road Test Package"
                                            value={values.roadTestPackage}
                                            error={errors.roadTestPackage}
                                            touched={touched.roadTestPackage}
                                        >
                                            {packages.map((roadTestPackage, key) => (
                                                <Field
                                                    key={roadTestPackage.package + key}
                                                    component={RadioButton}
                                                    name="roadTestPackage"
                                                    id={roadTestPackage.package}
                                                    label={roadTestPackage.package + " ($" + roadTestPackage.price + ")"}
                                                />
                                            ))}
                                        </RadioButtonGroup>
                                    </form>
                                );
                            }}
                        </Formik>
                    </Card.Body>
                </Card>
            </div>
        );
    }
}
